import React, { useRef, useEffect } from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import Tool, { type ToolButtonComponent } from './Tool';
import WhiteboardController from '../controllers/WhiteboardController';
import ToolButton from '../toolbars/ToolButton';
import emojiIcon from '../assets/icons/emoji.svg';
import { svgToIcon } from '../utils/svg';
import styles from './ToolAddReaction.module.css';

import { type Instance as TippyInstance } from 'tippy.js/index';
import SimpleGroup from 'fabric/SimpleGroup';
import {
  hasReactionBehaviors,
  hasEmbeddedTextBehaviors,
} from '../objects/guards';

interface EmojiPopupProps {
  selection: SimpleGroup | fabric.Object;
  hidePopup: () => void;
}

const Popup: React.FC<EmojiPopupProps> = ApplicationView(
  ({ selection, hidePopup }) => {
    const controller = useController(WhiteboardController);

    const toggleReaction = (obj, shortcodes, action?: 'add' | 'remove') => {
      if (typeof obj.toggleReaction !== 'function') return;
      obj.toggleReaction(
        {
          shortCode: shortcodes,
          userId: controller.state.userId,
        },
        action
      );
    };

    const onSelect = emoji => {
      const { shortcodes } = emoji;

      if (selection instanceof SimpleGroup) {
        let hasReaction = true;

        selection.forEachObjectDeep(obj => {
          if (
            hasReactionBehaviors(obj) &&
            !obj.hasReaction({
              shortCode: shortcodes,
              userId: controller.state.userId,
            })
          ) {
            hasReaction = false;
          }
        });
        selection.forEachObjectDeep(obj => {
          toggleReaction(obj, shortcodes, hasReaction ? 'remove' : 'add');
        });
      } else {
        toggleReaction(selection, shortcodes);
      }

      controller.canvas.requestRenderAll();
      hidePopup();
      Tool.toolActionComplete({ controller });
    };

    // @ts-expect-error Data does have a categories key
    const { categories } = data;
    useEffect(() => {
      while (categories[1]?.id === 'frequent') {
        categories.shift();
      }
    }, [categories]);

    return (
      <div className={styles.pickerContainer}>
        <Picker
          data={data}
          autoFocus
          theme='light'
          onEmojiSelect={onSelect}
          onClickOutside={() => controller?.canvas?.requestRenderAll()}
          maxFrequentRows={1}
        />
      </div>
    );
  }
);

const AddReactionButton: ToolButtonComponent = ({ selection, tool }) => {
  const tippyRef = useRef<TippyInstance>();
  const maybeStopEditing = () => {
    if (hasEmbeddedTextBehaviors(selection)) {
      selection.endTextEditing();
    }

    window.AhaAnalytics?.track(
      'Feature-7109872315285288858',
      'User views reaction list',
      {}
    );
  };

  return (
    <ToolButton
      onClick={maybeStopEditing}
      tippyRef={tippyRef}
      tippyIsEmojiPicker={true}
      tool={tool}
      popup={
        <Popup
          selection={selection}
          hidePopup={() => tippyRef?.current?.hide()}
        />
      }
    >
      {svgToIcon(emojiIcon)}
    </ToolButton>
  );
};

class ToolAddReaction extends Tool {
  static toolName = 'Add reaction';

  static buttonComponent() {
    return AddReactionButton;
  }
}

export default ToolAddReaction;
